/**
 * The page header — the one shape that repeats on all ~90 screens, so it is
 * where a change in typographic voice is felt hardest.
 *
 * Three rules:
 *   · the title is set at a display size with optical tracking, not at "big
 *     bold body". Tightened tracking at 28px is what separates a heading that
 *     was designed from one that was merely sized;
 *   · the subtitle is a caption, not a paragraph — capped at ~68ch so it stops
 *     where the eye does;
 *   · a hairline closes the block. It gives every page the same opening beat,
 *     and it is the cheapest way to make a screen read as composed rather than
 *     dropped in. `bare` opts out for heads that sit inside another frame.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface PageHeadProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Micro-label above the title — section, entity type, or document number. */
  eyebrow?: ReactNode;
  actions?: ReactNode;
  /** Drop the closing hairline (for heads that sit inside another frame). */
  bare?: boolean;
  className?: string;
}

export function PageHead({
  title,
  subtitle,
  eyebrow,
  actions,
  bare,
  className,
}: PageHeadProps) {
  return (
    <div
      className={cn(
        'mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between',
        bare ? 'sm:mb-7' : 'border-b border-border/70 pb-5 sm:mb-7 sm:pb-6',
        className,
      )}
    >
      <div className="min-w-0 space-y-1">
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h1 className="text-[1.5rem] leading-[1.15] font-semibold tracking-tight text-balance sm:text-[1.75rem]">
          {title}
        </h1>
        {subtitle ? (
          <p className="max-w-[68ch] text-sm text-muted-foreground text-pretty">{subtitle}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:pt-1">{actions}</div>
      ) : null}
    </div>
  );
}
